import { createAsyncThunk } from "@reduxjs/toolkit";
import { ApiResponse } from "../../../types/APIResponse";
import api from "../../../utils/constants/api";
import { apiEndpoints } from "../../../utils/constants/apiEndpoints";
import { Contact } from "../../../types";

export interface DeleteContact extends ApiResponse<any> {
    id: string;
}

// Fetch all contacts
export const fetchContacts = createAsyncThunk<
    Contact[],
    { establishmentLocationId?: string; placeId?: string; status?: number },
    { rejectValue: string }
>(
    'contact/fetchAll',
    async ({ establishmentLocationId, placeId, status }, { rejectWithValue }) => {
        try {
            const params = new URLSearchParams();
            if (establishmentLocationId) params.append('establishmentLocationId', establishmentLocationId);
            if (placeId) params.append('placeId', placeId);
            if (status !== undefined) params.append('status', status.toString());

            const response = await api.get<ApiResponse<Contact[]>>(
                `${apiEndpoints.contact.getAll}?${params.toString()}`
            );
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.message || 'Failed to fetch contacts');
        }
    }
);

// Fetch single contact
export const fetchSingleContact = createAsyncThunk<
    Contact,
    string,
    { rejectValue: string }
>(
    'contact/fetchSingle',
    async (id, { rejectWithValue }) => {
        try {
            const response = await api.get<ApiResponse<Contact>>(
                `${apiEndpoints.contact.get}/${id}`
            );
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.message || 'Failed to fetch contact');
        }
    }
);

// Add contact
export const addContact = createAsyncThunk<
    Contact,
    Contact,
    { rejectValue: string }
>(
    'contact/add',
    async (contactData, { rejectWithValue }) => {
        try {
            const response = await api.post<ApiResponse<Contact>>(
                apiEndpoints.contact.create,
                { data: contactData }
            );
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.message || 'Failed to add contact');
        }
    }
);

// Update contact
export const updateContact = createAsyncThunk<
    Contact,
    { id: string; data: Partial<Contact> },
    { rejectValue: string }
>(
    'contact/update',
    async ({ id, data }, { rejectWithValue }) => {
        try {
            const response = await api.put<ApiResponse<Contact>>(
                `${apiEndpoints.contact.update}/${id}`,
                { data }
            );
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.message || 'Failed to update contact');
        }
    }
);

// Delete contact
export const deleteContact = createAsyncThunk<
    DeleteContact,
    { id: string },
    { rejectValue: string }
>(
    'contact/delete',
    async ({ id }, { rejectWithValue }) => {
        try {
            const response = await api.delete<ApiResponse<DeleteContact>>(
                `${apiEndpoints.contact.delete}/${id}`
            );
            return { ...response.data, id };
        } catch (error: any) {
            return rejectWithValue(error.message || 'Failed to delete contact');
        }
    }
);

// Set primary contact
export const setPrimaryContact = createAsyncThunk<
    Contact,
    { id: string; establishmentLocationId?: string; placeId?: string },
    { rejectValue: string }
>(
    'contact/setPrimary',
    async ({ id, establishmentLocationId, placeId }, { rejectWithValue }) => {
        try {
            const params = new URLSearchParams();
            if (establishmentLocationId) params.append('establishmentLocationId', establishmentLocationId);
            if (placeId) params.append('placeId', placeId);

            const response = await api.put<ApiResponse<Contact>>(
                `${apiEndpoints.contact.setPrimary}/${id}?${params.toString()}`
            );
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.message || 'Failed to set primary contact');
        }
    }
);

// Update order
export const updateContactOrder = createAsyncThunk<
    Contact[],
    { orderUpdates: Array<{ id: string; index: number }> },
    { rejectValue: string }
>(
    'contact/updateOrder',
    async ({ orderUpdates }, { rejectWithValue }) => {
        try {
            const response = await api.put<ApiResponse<Contact[]>>(
                apiEndpoints.contact.updateOrder,
                { data: { orderUpdates } }
            );
            if (response?.success) {
                return response.data;
            } else {
                throw new Error(response.message || 'Failed to update order');
            }
        } catch (error: any) {
            return rejectWithValue(error.message || 'Failed to update order');
        }
    }
);
